/**
 * DataTable — Toolbar (Search/Filter/Refresh/Action) + Table als ein
 * Bauteil. Nutzt das existierende `Table` aus packages/ui darunter.
 *
 * Designed für Staff-Pages, die durchgängig "Liste mit Suche, Filter,
 * Refresh und 1-2 Action-Buttons" sind.
 *
 * Beispiel:
 *   <DataTable<TToken>
 *     columns={[
 *       { key: 'description', header: 'Beschreibung', sortable: true },
 *       { key: 'status', header: 'Status', render: (t) => <StatusBadge status={t.status} /> },
 *       { key: 'created_at', header: 'Erstellt', sortable: true },
 *     ]}
 *     data={tokens}
 *     keyExtractor={(t) => t.id}
 *     loading={loading}
 *     error={error}
 *     search={{ value: search, onChange: setSearch, placeholder: 'Beschreibung suchen…' }}
 *     filters={[
 *       { key: 'status', label: 'Status', value: filter, options: [...], onChange: setFilter },
 *     ]}
 *     onRefresh={refetch}
 *     primaryAction={{ label: 'Neuer Token', onClick: () => setCreateOpen(true) }}
 *   />
 */

import type { ReactNode } from 'react'
import { Table, type TableColumn } from '../Table'
import './DataTable.css'

export interface DataTableSearch {
  value: string
  onChange: (next: string) => void
  placeholder?: string
}

export interface DataTableFilterOption {
  value: string
  label: string
}

export interface DataTableFilter {
  key: string
  label: string
  value: string
  options: DataTableFilterOption[]
  onChange: (next: string) => void
}

export interface DataTableAction {
  label: ReactNode
  onClick: () => void
  variant?: 'default' | 'secondary'
  disabled?: boolean
}

export interface DataTableProps<T> {
  columns: TableColumn<T>[]
  data: T[]
  keyExtractor: (row: T, index: number) => string
  loading?: boolean
  error?: { message: string } | null
  emptyMessage?: string
  /** Filter-Funktion die auf data angewandt wird BEVOR sortiert wird
   * (lokales Filtern). Wenn weggelassen, werden alle Rows gezeigt. */
  filterPredicate?: (row: T) => boolean
  search?: DataTableSearch
  filters?: DataTableFilter[]
  onRefresh?: () => void
  primaryAction?: DataTableAction
  secondaryAction?: DataTableAction
  /** Row-Click handler (von Table durchgereicht) */
  onRowClick?: (row: T, index: number) => void
  /** Anzahl-Hint rechts neben dem Filter (z.B. "5 von 12 Tokens") */
  countHint?: ReactNode
  className?: string
}

export function DataTable<T>({
  columns,
  data,
  keyExtractor,
  loading = false,
  error = null,
  emptyMessage,
  filterPredicate,
  search,
  filters,
  onRefresh,
  primaryAction,
  secondaryAction,
  onRowClick,
  countHint,
  className = '',
}: DataTableProps<T>) {
  const filtered = filterPredicate ? data.filter(filterPredicate) : data

  return (
    <div className={`data-table ${className}`.trim()}>
      <div className="data-table-toolbar">
        {search && (
          <div className="data-table-search">
            <span className="data-table-search-icon" aria-hidden>🔍</span>
            <input
              type="search"
              value={search.value}
              onChange={(e) => search.onChange(e.target.value)}
              placeholder={search.placeholder ?? 'Suchen…'}
              className="data-table-search-input"
            />
          </div>
        )}

        {filters?.map((f) => (
          <select
            key={f.key}
            value={f.value}
            onChange={(e) => f.onChange(e.target.value)}
            aria-label={f.label}
            className="data-table-filter"
          >
            {f.options.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        ))}

        {countHint && <span className="data-table-count-hint">{countHint}</span>}

        <div className="data-table-toolbar-spacer" />

        {onRefresh && (
          <button
            type="button"
            className="data-table-btn data-table-btn-icon"
            onClick={onRefresh}
            disabled={loading}
            title="Aktualisieren"
            aria-label="Liste aktualisieren"
          >
            ↻
          </button>
        )}

        {secondaryAction && (
          <button
            type="button"
            className="data-table-btn data-table-btn-secondary"
            onClick={secondaryAction.onClick}
            disabled={secondaryAction.disabled}
          >
            {secondaryAction.label}
          </button>
        )}

        {primaryAction && (
          <button
            type="button"
            className="data-table-btn data-table-btn-primary"
            onClick={primaryAction.onClick}
            disabled={primaryAction.disabled}
          >
            {primaryAction.label}
          </button>
        )}
      </div>

      {error && (
        <div className="data-table-error" role="alert">
          {error.message}
        </div>
      )}

      <Table<T>
        columns={columns}
        data={filtered}
        keyExtractor={keyExtractor}
        loading={loading}
        emptyMessage={emptyMessage ?? 'Keine Einträge'}
        onRowClick={onRowClick}
        hoverable
        stickyHeader
      />
    </div>
  )
}

export default DataTable
